import { TConsentManagement, TConsentManagementInstance, TCategoryConsentState } from './ConsentManagement';
import { isDOMElementFocusable } from 'widgets/toolbox/util';

declare const window: ExtendedWindow & {
    Optanon: {
        ToggleInfoDisplay: () => void;
    };
    OptanonReady: boolean;
    OptanonWrapper: () => void;
    OnetrustActiveGroups: string;
    OneTrust: {
        UpdateConsent: (category: string, consentString: string) => void;
    };
};

type TGenericPrefs = ReturnType<TConsentManagementInstance['prefs']>;
type TOTPrefs = TGenericPrefs & {
    settings: {
        onetrustCategoriesMapping: Record<string, string>;
        privacyRightsBlockedCookiesLevelsMapper: Record<string, Array<number>>;
    };
};

const BANNER_CONTAINER_SELECTOR = '#onetrust-banner-sdk';
const CONSENT_CONTAINER_SELECTOR = '#onetrust-pc-sdk';
const BANNER_DIALOG_SELECTOR = '#onetrust-banner-sdk [role="dialog"]';
const CONSENT_DIALOG_SELECTOR = '#onetrust-pc-sdk [role="dialog"]';
const ONETRUST_CONSENT_ROOT_SELECTOR = '#onetrust-consent-sdk';
const HEADER_ID = 'headercontent';
const ARIA_HIDDEN = 'aria-hidden';
const ARIA_MODAL = 'aria-modal';
const RESTORE_ARIA_MODAL = 'data-restore-aria-modal';

/**
 * @param ConsentManagement Base widget for extending
 * @returns OneTrustCM class
 */
function OneTrustCMClassCreator(ConsentManagement: TConsentManagement) {
    class OneTrustCM extends ConsentManagement {
        manageConsentContainer: HTMLElement | null = null;

        cookieBannerContainer: HTMLElement | null = null;

        manageConsentDialog: HTMLElement | null = null;

        cookieBannerDialog: HTMLElement | null = null;

        ariaHiddenState = new Map<Element, string | null>();

        activeFocusTrapDialog: HTMLElement | null = null;

        backgroundObserver: MutationObserver | null = null;

        focusTrapListenersInitialized = false;

        isAccessibilityFocusTrapNavigation = false;

        setFocusCallback = () => {};

        private focusEventCallback = (event: FocusEvent): void => {
            this.handleFocusTrapEscape(event);
            this.isAccessibilityFocusTrapNavigation = true;
        };

        private pointerEventCallback = (): void => {
            this.isAccessibilityFocusTrapNavigation = false;
        };

        prefs() {
            const prefs = {
                ...super.prefs()
            } as TOTPrefs;

            prefs.settings.onetrustCategoriesMapping ||= {};

            return prefs;
        }

        /**
         * @description OneTrust specific initialization logic
         * This method initializes the OneTrust consent provider
         * and sets up event listeners for consent changes.
         */
        initializeConsentProvider() {
            window.addEventListener('OneTrustGroupsUpdated', event => {
                const consentDetails = (event as CustomEvent<Array<string>>).detail;

                this.saveConsentState(consentDetails);
            });

            const activeGroups = window.OnetrustActiveGroups;
            const activeGroupsArray = activeGroups ? activeGroups.split(',').filter(group => group.length > 0) : [];

            this.saveConsentState(activeGroupsArray);

            if (window.OptanonReady) {
                this.focusCookieBannerOnLoad();
            } else if (window.OptanonWrapper) {
                const originalOptanonWrapper = window.OptanonWrapper;

                window.OptanonWrapper = () => {
                    originalOptanonWrapper();
                    this.focusCookieBannerOnLoad();
                    window.OptanonWrapper = originalOptanonWrapper;
                };
            }

            window.addEventListener('OneTrustPCLoaded', () => {
                this.openManageConsentDialog();
            });

            window.addEventListener('message', event => {
                if (event.origin !== 'https://privacyportal.onetrust.com') {
                    return;
                }

                const requestTypes = JSON.parse(event.data)?.requestTypes;
                const blockedCookiesLevelsMapper = this.prefs().settings.privacyRightsBlockedCookiesLevelsMapper;
                const categoriesToBlock = requestTypes?.reduce((acc: Array<number>, requestType: string) => {
                    const blockedLevels = blockedCookiesLevelsMapper[requestType];

                    if (blockedLevels) {
                        acc.push(...blockedLevels);
                    }

                    return acc;
                }, []);

                const uniqueCategoriesToBlock = Array.from(new Set(categoriesToBlock));
                const updateString = uniqueCategoriesToBlock.map(cat => `C000${cat}:0`).join(',');

                window.OneTrust.UpdateConsent('Category', updateString);
            });
        }

        /**
         * @description Transforms the OneTrust consent to generic one and
         * saves the consent state based on the provided consent details.
         *
         * @param activeGroups - Array of active group IDs from OneTrust.
         */
        saveConsentState(activeGroups: Array<string>) {
            const categoriesMapping = this.prefs().settings.onetrustCategoriesMapping;
            const consentState: TCategoryConsentState = {
                ESSENTIAL: false,
                FUNCTIONAL: false,
                MARKETING: false
            };

            activeGroups.forEach(groupId => {
                const category = categoriesMapping[groupId];

                if (category) {
                    consentState[category] = true;
                }
            });

            this.updateCategoryConsentState(consentState);
        }

        /**
         * @description Opens the OneTrust settings page.
         * This method is called when the user clicks on the settings link.
         * And is triggered from the parent widget.
         */
        openSettings() {
            if (!window.Optanon) {
                // eslint-disable-next-line no-console
                console.warn('OneTrust consent provider is not available.');

                return;
            }

            window.Optanon.ToggleInfoDisplay();
        }

        /** ADA FRONT END ADAPTATIONS */

        /**
         * @description Initialize the values of repeatedly used modals and modal containers.
         */
        setContainers() {
            this.cookieBannerContainer = document.querySelector<HTMLElement>(BANNER_CONTAINER_SELECTOR);
            this.manageConsentContainer = document.querySelector<HTMLElement>(CONSENT_CONTAINER_SELECTOR);
            this.cookieBannerDialog = document.querySelector<HTMLElement>(BANNER_DIALOG_SELECTOR);
            this.manageConsentDialog = document.querySelector<HTMLElement>(CONSENT_DIALOG_SELECTOR);
        }

        /**
         * @description Focuses the OneTrust banner after it is initialized to improve keyboard accessibility.
         */
        focusCookieBannerOnLoad() {
            this.setContainers();

            if (!this.cookieBannerDialog) {
                return;
            }

            this.initializeFocusTrapListeners();

            this.setCookieBannerAriaProps();

            this.setFocusCallback = this.focusCookieBanner.bind(this);

            this.activateDialog(this.cookieBannerDialog);

            document.addEventListener('click', e => {
                this.handleModalAction(e);
            });
        }

        /**
         * @description Moves focus to the cookie banner when it's active.
         */
        focusCookieBanner() {
            this.cookieBannerDialog?.focus({ preventScroll: true });
        }

        /**
         * @description Adds dialog relationship attributes to the cookie banner settings button.
         */
        setCookieBannerAriaProps() {
            const settingsButton = this.cookieBannerDialog?.querySelector<HTMLElement>('#onetrust-pc-btn-handler');

            settingsButton?.setAttribute('aria-haspopup', 'dialog');
            settingsButton?.setAttribute('aria-controls', 'onetrust-pc-sdk');
        }

        /**
         * @description Focuses the OneTrust banner after it is initialized to improve keyboard accessibility.
         */
        focusConsentDialogLogo() {
            const logo = document.querySelector<HTMLElement>('.ot-pc-logo img');

            if (logo) {
                this.manageConsentDialog?.removeAttribute('aria-hidden');
                logo.setAttribute('tabindex', '-1');
                logo.focus();
            }
        }

        /**
         * @description Toggles the aria-hidden and aria-modal properties
         * between the cookie banner and the Manage Preferences
         * dialog.
         *
         * @param event - click event
         * @param event.target - target button being clicked
         */
        handleModalAction(event) {
            const target = event.target as Element;
            const isConsentDialogClosed = target.id === 'close-pc-btn-handler';

            if (isConsentDialogClosed) {
                this.setFocusCallback = this.focusCookieBanner.bind(this);
                this.activateDialog(this.cookieBannerDialog);

                return;
            }

            const isConsentAccepted = target.id === 'onetrust-accept-btn-handler';
            const isCookieBannerClosed = target.classList.contains('onetrust-close-btn-handler');

            if (isConsentAccepted || isCookieBannerClosed) {
                this.closeOneTrustDialogs();
            }
        }

        /**
         * @description Opens the OneTrust Manage Preferences dialog, focuses its logo,
         * activates its modal accessibility state, and raises it above the banner.
         */
        openManageConsentDialog() {
            if (!this.manageConsentDialog) {
                this.setContainers();
            }

            this.setFocusCallback = this.focusConsentDialogLogo.bind(this);
            this.activateDialog(this.manageConsentDialog);
            this.manageConsentContainer?.style.setProperty('z-index', '2147483646');
            this.manageConsentContainer?.querySelector('#ot-pc-content')?.removeAttribute('tabIndex');
        }

        /**
         * @description Closes all OneTrust modal surfaces and restores accessibility state
         * that was changed while OneTrust was active.
         */
        closeOneTrustDialogs() {
            this.clearOneTrustAriaModal();
            this.disconnectBackgroundObserver();
            this.restoreAriaHidden();
            this.restoreAriaModals();

            document.removeEventListener('focusin', this.focusEventCallback, true);

            ['pointerdown', 'mousedown', 'touchstart'].forEach(eventName => {
                document.removeEventListener(eventName, this.pointerEventCallback);
            });

            const header = document.getElementById(HEADER_ID);

            if (this.isAccessibilityFocusTrapNavigation && header) {
                header.setAttribute('tabindex', '-1');
                header.focus();
            }
        }

        /**
         * @description Removes OneTrust aria-modal state and disables the active focus-trap
         * dialog reference.
         */
        clearOneTrustAriaModal() {
            this.activeFocusTrapDialog = null;

            [this.manageConsentDialog, this.cookieBannerDialog].forEach(dialog => {
                dialog?.removeAttribute(ARIA_MODAL);
                dialog?.removeAttribute(RESTORE_ARIA_MODAL);
            });
        }

        /**
         * @description Restores aria-modal on any application modal that was active
         * before OneTrust took modal ownership.
         */
        restoreAriaModals() {
            document.querySelectorAll(`[${RESTORE_ARIA_MODAL}="true"]`).forEach(el => {
                el.setAttribute(ARIA_MODAL, 'true');
                el.removeAttribute(RESTORE_ARIA_MODAL);
            });
        }

        /**
         * @description Makes the provided OneTrust dialog the active modal and updates
         * aria-modal, aria-hidden, and focus-trap state accordingly.
         *
         * @param dialog - OneTrust dialog to activate.
         */
        activateDialog(dialog: HTMLElement | null) {
            if (!dialog) {
                return;
            }

            dialog.removeAttribute(RESTORE_ARIA_MODAL);
            dialog.setAttribute(ARIA_MODAL, 'true');

            document.querySelectorAll(`[${ARIA_MODAL}="true"]`).forEach(el => {
                if (el === dialog) {
                    return;
                }

                el.removeAttribute(ARIA_MODAL);

                if (!el.matches(`${BANNER_DIALOG_SELECTOR}, ${CONSENT_DIALOG_SELECTOR}`)) {
                    el.setAttribute(RESTORE_ARIA_MODAL, 'true');
                }
            });

            this.activeFocusTrapDialog = dialog;
            this.setBackgroundAriaHidden(dialog);
            this.setOneTrustDialogsAriaHidden(dialog);
        }

        /** Background needs to be aria-hidden for VoiceOver.
         * VoiceOver is inconsistent and aria-modal property is not
         * correctly controlling the active document.
         */

        /**
         * @description Hides body-level siblings from assistive technology while keeping
         * the element that contains the active OneTrust dialog exposed.
         *
         * @param dialog - Active OneTrust dialog.
         */
        setBackgroundAriaHidden(dialog: HTMLElement) {
            const oneTrustConsentRoot = document.querySelector<HTMLElement>(ONETRUST_CONSENT_ROOT_SELECTOR);

            this.removeActiveDialogAriaHidden(dialog, oneTrustConsentRoot);
            this.setFocusCallback();
            this.hideBackgroundElements(oneTrustConsentRoot);
            this.observeBackgroundElements(oneTrustConsentRoot);
        }

        /**
         * @description Exposes the active OneTrust dialog and its root container to
         * assistive technology.
         *
         * @param dialog - Active OneTrust dialog.
         * @param oneTrustConsentRoot - Root OneTrust consent element.
         */
        removeActiveDialogAriaHidden(dialog: HTMLElement, oneTrustConsentRoot: HTMLElement | null) {
            if (oneTrustConsentRoot) {
                this.setAndStoreAriaHidden(oneTrustConsentRoot, false);
            }

            this.setAndStoreAriaHidden(dialog, false);
        }

        /**
         * @description Hides body-level elements outside the OneTrust consent root from
         * assistive technology.
         *
         * @param oneTrustConsentRoot - Root OneTrust consent element.
         */
        hideBackgroundElements(oneTrustConsentRoot: HTMLElement | null) {
            Array.from(document.body.children).forEach(element => {
                if (element === oneTrustConsentRoot) {
                    return;
                }

                this.setAndStoreAriaHidden(element, true);
            });
        }

        /**
         * @description Hides elements added after page load from assistive technology.
         * Elements such as recaptcha are added after OneTrust sets background
         * aria-* properties, making those accessible to VoiceOver.
         * @param oneTrustConsentRoot - Root OneTrust consent element.
         */
        observeBackgroundElements(oneTrustConsentRoot: HTMLElement | null) {
            this.disconnectBackgroundObserver();

            this.backgroundObserver = new MutationObserver(mutations => {
                const dialog = this.getActiveFocusTrapDialog();

                if (!dialog) {
                    return;
                }

                mutations.forEach(mutation => {
                    mutation.addedNodes.forEach(node => {
                        if (!(node instanceof Element) || node === oneTrustConsentRoot || node.contains(dialog)) {
                            return;
                        }

                        this.setAndStoreAriaHidden(node, true);
                    });
                });
            });

            this.backgroundObserver.observe(document.body, { childList: true });
        }

        /**
         * @description Disconnect observer after OneTrust modals close.
         */
        disconnectBackgroundObserver() {
            this.backgroundObserver?.disconnect();
            this.backgroundObserver = null;
        }

        /**
         * @description Synchronizes aria-hidden between the OneTrust banner and consent
         * dialog so only the active dialog is exposed.
         *
         * @param activeDialog - Currently active OneTrust dialog.
         */
        setOneTrustDialogsAriaHidden(activeDialog: HTMLElement) {
            [this.manageConsentDialog, this.cookieBannerDialog].forEach(dialog => {
                if (dialog) {
                    this.setAndStoreAriaHidden(dialog, dialog !== activeDialog);
                }
            });
        }

        /**
         * @description Sets aria-hidden on an element and stores the original value so it
         * can be restored when OneTrust closes.
         *
         * @param element - Element whose aria-hidden value should change.
         * @param isHidden - Whether the element should be hidden from assistive technology.
         */
        setAndStoreAriaHidden(element: Element, isHidden: boolean) {
            if (!this.ariaHiddenState.has(element)) {
                this.ariaHiddenState.set(element, element.getAttribute(ARIA_HIDDEN));
            }

            element.setAttribute(ARIA_HIDDEN, String(isHidden));
        }

        /**
         * @description Restores a single element's aria-hidden value to the value captured
         * before OneTrust changed it.
         *
         * @param element - Element whose aria-hidden value should be restored.
         */
        restoreAriaHiddenElement(element: Element) {
            if (!this.ariaHiddenState.has(element)) {
                return;
            }

            const originalValue = this.ariaHiddenState.get(element);

            if (originalValue === null) {
                element.removeAttribute(ARIA_HIDDEN);
            } else if (originalValue !== undefined) {
                element.setAttribute(ARIA_HIDDEN, originalValue);
            }
        }

        /**
         * @description Restores all aria-hidden values changed while OneTrust was active.
         */
        restoreAriaHidden() {
            this.ariaHiddenState.forEach((_, element) => {
                this.restoreAriaHiddenElement(element);
            });
            this.ariaHiddenState.clear();
        }

        /**
         * @description Initializes document-level focus trap listeners once. The listeners
         * only redirect focus while an active OneTrust dialog is registered.
         */
        initializeFocusTrapListeners() {
            if (this.focusTrapListenersInitialized) {
                return;
            }

            this.focusTrapListenersInitialized = true;
            this.isAccessibilityFocusTrapNavigation = true;

            document.addEventListener(
                'keydown',
                event => {
                    if (event.key === 'Tab') {
                        this.handleFocusTrapTab(event);
                    }
                },
                true
            );

            // Escape regular mouse events from focus trap
            ['pointerdown', 'mousedown', 'touchstart'].forEach(eventName => {
                document.addEventListener(eventName, this.pointerEventCallback);
            });

            document.addEventListener('focusin', this.focusEventCallback, true);
        }

        /**
         * @description Gets the current OneTrust dialog that should own keyboard focus.
         *
         * @returns Active focus-trap dialog, or null when OneTrust is not modal or when the
         * dialog has nothing focusable to trap.
         */
        getActiveFocusTrapDialog(): HTMLElement | null {
            const dialog = this.activeFocusTrapDialog;

            if (dialog?.getAttribute(ARIA_MODAL) === 'true' && this.getFocusableElements(dialog).length) {
                return dialog;
            }

            return null;
        }

        /**
         * @description Keeps Tab and Shift+Tab navigation inside the active OneTrust dialog.
         *
         * @param event - Keyboard event from the document-level keydown listener.
         */
        handleFocusTrapTab(event: KeyboardEvent) {
            const dialog = this.getActiveFocusTrapDialog();

            if (!dialog) {
                return;
            }

            const focusableElements = this.getFocusableElements(dialog);

            if (!focusableElements.length) {
                return;
            }

            const lastIndex = focusableElements.length - 1;
            const currentIndex = focusableElements.indexOf(document.activeElement as HTMLElement);
            let nextIndex;

            if (currentIndex === -1) {
                nextIndex = event.shiftKey ? lastIndex : 0;
            } else if (event.shiftKey) {
                nextIndex = currentIndex === 0 ? lastIndex : currentIndex - 1;
            } else {
                nextIndex = currentIndex === lastIndex ? 0 : currentIndex + 1;
            }

            event.preventDefault();
            focusableElements[nextIndex].focus();
        }

        /**
         * @description Redirects focus back into OneTrust if focus lands outside the active
         * dialog while OneTrust is modal.
         *
         * @param event - Focus event from the document-level focusin listener.
         */
        handleFocusTrapEscape(event: FocusEvent) {
            const dialog = this.getActiveFocusTrapDialog();
            const target = event.target;

            if (!this.isAccessibilityFocusTrapNavigation || !dialog || !(target instanceof Element) || dialog.contains(target)) {
                return;
            }

            this.focusFirstDescendant(dialog);
        }

        /**
         * @description Focuses the first focusable descendant in the dialog, falling back
         * to the dialog container when no focusable children exist.
         *
         * @param dialog - Dialog that should receive focus.
         */
        focusFirstDescendant(dialog: HTMLElement) {
            const firstElement = this.getFocusableElements(dialog)[0];

            if (firstElement) {
                firstElement.focus();
            } else {
                dialog.tabIndex = -1;
                dialog.focus();
            }
        }

        /**
         * @description Finds visible, enabled focusable elements inside a container.
         *
         * @param container - Container whose focusable descendants should be returned.
         * @returns Focusable descendants in DOM order.
         */
        getFocusableElements(container: HTMLElement): Array<HTMLElement> {
            const FOCUSABLE_ELEMENTS_SELECTOR = ['a[href]', 'button', 'input', 'select', 'textarea', 'iframe', '[tabindex]'].join(',');

            return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_ELEMENTS_SELECTOR)).filter(element => {
                return !element.closest('[hidden], [aria-hidden="true"]') && isDOMElementFocusable(element);
            });
        }
    }

    return OneTrustCM;
}

export type TOneTrustCM = ReturnType<typeof OneTrustCMClassCreator>;

export type TOneTrustCMInstance = InstanceType<TOneTrustCM>;

export default OneTrustCMClassCreator;
